import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useCustomerWorkspace } from '../../../context/CustomerWorkspaceContext';
import AnalyticsSummaryWidget from './AnalyticsSummaryWidget';
import PostAnalyticsTable from './PostAnalyticsTable';
import AiVisibilityDashboard from './AiVisibilityDashboard';
import DateRangeFilter from '../.././../components/Shared/DateRangeFilter';
import {
  fetchAnalyticsSummary,
  fetchPostAnalyticsList,
  triggerAnalyticsCollection,
} from '../../../services/analyticsService';
import { fetchPostsByIds } from '../../../services/postService';
import { AnalyticsSummary, PostAnalytics } from '../../../types/analytics';
import { debug } from '../../../utils/debugLogger';
import { useFeatureAccess } from '../../feature-access/hooks/useFeatureAccess';
import { FeatureUpgradeModal } from '../../feature-access/components/FeatureUpgradeModal';

type PlatformFilter = 'ALL' | 'FACEBOOK' | 'INSTAGRAM' | 'INSTAGRAM_REELS' | 'LINKEDIN' | 'YOUTUBE' | 'YOUTUBE_SHORTS';

const PLATFORM_OPTIONS: { value: PlatformFilter; label: string }[] = [
  { value: 'ALL', label: 'All Platforms' },
  { value: 'FACEBOOK', label: 'Facebook' },
  { value: 'INSTAGRAM', label: 'Instagram' },
  { value: 'INSTAGRAM_REELS', label: 'Instagram Reels' },
  { value: 'LINKEDIN', label: 'LinkedIn (Business)' },
  { value: 'YOUTUBE', label: 'YouTube' },
  { value: 'YOUTUBE_SHORTS', label: 'YouTube Shorts' },
];

const toDateStr = (d: Date) => d.toISOString().split('T')[0];

const defaultStartDate = () => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return toDateStr(d);
};
const defaultEndDate = () => toDateStr(new Date());

const asNumber = (value: unknown): number | undefined => {
  if (value == null) return undefined;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    if (typeof record.N === 'string') {
      const parsed = Number(record.N);
      return Number.isFinite(parsed) ? parsed : undefined;
    }
    if (record.NULL === true) return undefined;
  }
  return undefined;
};

const normalizePlatform = (platform: string | undefined): Exclude<PlatformFilter, 'ALL'> | string => {
  const token = String(platform || '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');

  if (token === 'YOUTUBE_SHORT') return 'YOUTUBE_SHORTS';
  if (token === 'YOUTUBEVIDEO') return 'YOUTUBE';
  if (token === 'INSTAGRAM_REEL') return 'INSTAGRAM_REELS';
  return token;
};

const PLATFORM_GROUPS: Partial<Record<PlatformFilter, string[]>> = {
  INSTAGRAM: ['INSTAGRAM', 'INSTAGRAM_REELS'],
  FACEBOOK: ['FACEBOOK', 'FACEBOOK_VIDEO', 'FACEBOOK_REELS'],
};

const matchesPlatformFilter = (
  filterPlatform: PlatformFilter,
  itemPlatform: string | undefined
): boolean => {
  if (filterPlatform === 'ALL') return true;
  const normalized = normalizePlatform(itemPlatform);
  const group = PLATFORM_GROUPS[filterPlatform];
  if (group) return group.includes(normalized);
  return normalized === filterPlatform;
};

// ── Session cache (survives navigation; cleared when tab closes) ──────────────

const ANALYTICS_CACHE_TTL_MS = 30 * 60 * 1000;

interface AnalyticsCacheEntry {
  list: PostAnalytics[];
  summary: AnalyticsSummary;
}

function cacheKey(customerId: string, start: string, end: string) {
  // v2: invalidates entries cached before the Facebook video_views sanitization
  return `analytics_cache_v2_${customerId}_${start}_${end}`;
}

function readAnalyticsCache(customerId: string, start: string, end: string): AnalyticsCacheEntry | null {
  try {
    const raw = sessionStorage.getItem(cacheKey(customerId, start, end));
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw) as { data: AnalyticsCacheEntry; ts: number };
    if (Date.now() - ts > ANALYTICS_CACHE_TTL_MS) return null;
    return data;
  } catch { return null; }
}

function writeAnalyticsCache(
  customerId: string, start: string, end: string,
  list: PostAnalytics[], summary: AnalyticsSummary
) {
  try {
    sessionStorage.setItem(
      cacheKey(customerId, start, end),
      JSON.stringify({ data: { list, summary }, ts: Date.now() })
    );
  } catch { /* quota exceeded — ignore */ }
}

// ── Component ─────────────────────────────────────────────────────────────────

type AnalyticsView = 'social' | 'ai';

/** Segmented switch between the two Analytics reports (SGL-140). */
const AnalyticsViewToggle: React.FC<{
  view: AnalyticsView;
  onChange: (v: AnalyticsView) => void;
  aiLocked: boolean;
  onLockedClick: () => void;
}> = ({ view, onChange, aiLocked, onLockedClick }) => (
  <div
    className="flex items-center gap-0.5 border-b border-theme-border"
    role="tablist"
    aria-label="Analytics report"
  >
    {([
      { key: 'social', label: 'Social Posts' },
      { key: 'ai', label: 'AI Visibility' },
    ] as { key: AnalyticsView; label: string }[]).map((t) => {
      const locked = t.key === 'ai' && aiLocked;
      return (
        <button
          key={t.key}
          role="tab"
          aria-selected={view === t.key}
          onClick={() => (locked ? onLockedClick() : onChange(t.key))}
          className={`-mb-px flex items-center gap-1.5 border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${
            view === t.key
              ? 'border-brand-primary text-theme-fg'
              : 'border-transparent text-theme-fg-muted hover:text-theme-fg'
          }`}
        >
          {t.label}
          {locked && (
            <span className="rounded-full bg-brand-primary/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brand-primary">
              Pro
            </span>
          )}
        </button>
      );
    })}
  </div>
);

const AnalyticsDashboard: React.FC = () => {
  const { idToken } = useAuth();
  const { selectedCustomerId } = useCustomerWorkspace();
  const [analyticsView, setAnalyticsView] = useState<AnalyticsView>('social');
  const [showVisibilityUpgradeModal, setShowVisibilityUpgradeModal] = useState(false);
  const { canAccessFeature } = useFeatureAccess();
  const visibilityManagerLocked = !canAccessFeature('visibility_manager');
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [analyticsList, setAnalyticsList] = useState<PostAnalytics[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [noData, setNoData] = useState(false);
  const [filterPlatform, setFilterPlatform] = useState<PlatformFilter>('ALL');
  const [platformMenuOpen, setPlatformMenuOpen] = useState(false);
  const platformDropdownRef = useRef<HTMLDivElement>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const collectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const collectCancelRef = useRef<(() => void) | null>(null);
  const [collecting, setCollecting] = useState(false);
  const [collectMessage, setCollectMessage] = useState<string | null>(null);

  // Date range state — defaults to last 30 days so data loads automatically on mount
  const [startDate, setStartDate] = useState<string>(defaultStartDate);
  const [endDate, setEndDate] = useState<string>(defaultEndDate);

  useEffect(() => {
    if (!platformMenuOpen) return;
    const closeOnOutside = (e: MouseEvent) => {
      if (
        platformDropdownRef.current &&
        !platformDropdownRef.current.contains(e.target as Node)
      ) {
        setPlatformMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', closeOnOutside);
    return () => document.removeEventListener('mousedown', closeOnOutside);
  }, [platformMenuOpen]);

  const stopCollecting = useCallback(() => {
    if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; }
    if (collectTimeoutRef.current) { clearTimeout(collectTimeoutRef.current); collectTimeoutRef.current = null; }
    if (collectCancelRef.current) { collectCancelRef.current(); collectCancelRef.current = null; }
  }, []);

  // Clean up any in-flight poll/timeout on unmount
  useEffect(() => () => stopCollecting(), [stopCollecting]);

  const loadAnalytics = useCallback(
    async (customerId: string, start: string, end: string) => {
      if (!idToken) return;

      // Restore from cache immediately so data is visible on navigation back
      const cached = readAnalyticsCache(customerId, start, end);
      if (cached) {
        setSummary(cached.summary ?? null);
        setAnalyticsList(cached.list ?? []);
      } else {
        setLoading(true);
      }
      setError(null);
      setNoData(false);

      const TIMEOUT_MS = 30000;
      const timeout = <T,>(p: Promise<T>): Promise<T> =>
        Promise.race([
          p,
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('Request timed out — please retry.')), TIMEOUT_MS)
          ),
        ]);

      try {
        const [fetchedSummary, fetchedList] = await timeout(
          Promise.all([
            fetchAnalyticsSummary(customerId, idToken, { startDate: start, endDate: end }),
            fetchPostAnalyticsList(customerId, idToken, { startDate: start, endDate: end }),
          ])
        );

        // Fetch only the posts the analytics rows reference (decoration only —
        // a failure here shouldn't block the table)
        const postIds = Array.from(
          new Set(fetchedList.map((item) => item.post_id).filter(Boolean))
        );
        const fetchedPosts = postIds.length
          ? await timeout(fetchPostsByIds(customerId, postIds, idToken)).catch((err) => {
              debug.warn('Posts fetch failed (non-fatal):', err);
              return [];
            })
          : [];

        // Map posts to analytics items
        const postsMap = new Map(fetchedPosts.map((p) => [p.post_id, p]));
        const mappedList = fetchedList.map((item) => {
          const metrics = (item as any).metrics || {};
          const postRef = postsMap.get(item.post_id);
          // Rows collected before the backend fix stored post_media_view (all media,
          // photos included) as video_views on Facebook. Drop video metrics for
          // Facebook posts that aren't videos/reels.
          const subtype = String((postRef as any)?.platform || '').toUpperCase();
          const isStaleFbVideoViews =
            normalizePlatform(item.platform) === 'FACEBOOK' &&
            postRef != null &&
            !subtype.includes('VIDEO') &&
            !subtype.includes('REEL');
          return {
            ...item,
            platform: normalizePlatform(item.platform),
            impressions: asNumber((item as any).impressions) ?? asNumber(metrics.impressions),
            reach: asNumber((item as any).reach) ?? asNumber(metrics.reach),
            engagement: asNumber((item as any).engagement) ?? asNumber(metrics.engagement),
            likes: asNumber((item as any).likes) ?? asNumber(metrics.likes),
            comments: asNumber((item as any).comments) ?? asNumber(metrics.comments),
            shares: asNumber((item as any).shares) ?? asNumber(metrics.shares),
            saved: asNumber((item as any).saved) ?? asNumber(metrics.saved),
            video_views: isStaleFbVideoViews
              ? undefined
              : asNumber((item as any).video_views) ?? asNumber(metrics.video_views),
            reels_video_watch_time: isStaleFbVideoViews
              ? undefined
              : asNumber((item as any).reels_video_watch_time) ??
                asNumber(metrics.reels_video_watch_time),
            postReference: postRef,
          };
        });

        setSummary(fetchedSummary ?? null);
        setAnalyticsList(mappedList);
        // Backend returns a 200 summary of zeros when nothing matches — treat
        // an empty list as "no data" instead of rendering a dashboard of zeros
        setNoData(mappedList.length === 0);
        if (fetchedSummary) writeAnalyticsCache(customerId, start, end, mappedList, fetchedSummary);
      } catch (err: any) {
        debug.error('Error loading analytics dashboard:', err);
        if (!cached) {
          if (err?.message?.includes('timed out')) {
            setNoData(true);
          } else {
            setError('Failed to load analytics data. Please try again.');
          }
        }
      } finally {
        setLoading(false);
      }
    },
    [idToken]
  );

  // Client-side platform filter — switching platforms doesn't trigger a new API call
  const filteredList = useMemo(
    () => analyticsList.filter((item) => matchesPlatformFilter(filterPlatform, item.platform)),
    [analyticsList, filterPlatform]
  );

  // Summary totals always derived from the (sanitized, filtered) list so the
  // widget matches the table exactly — backend totals may include stale values.
  const computedSummary = useMemo((): AnalyticsSummary | null => {
    if (!summary) return null;

    const zero = {
      impressions: 0, reach: 0, engagement: 0,
      comments: 0, likes: 0, shares: 0, saved: 0,
      video_views: 0, reels_video_watch_time: 0,
    };
    const totals = filteredList.reduce(
      (acc, item) => ({
        impressions:            acc.impressions            + (item.impressions            ?? 0),
        reach:                  acc.reach                  + (item.reach                  ?? 0),
        engagement:             acc.engagement             + (item.engagement             ?? 0),
        comments:               acc.comments               + (item.comments               ?? 0),
        likes:                  acc.likes                  + (item.likes                  ?? 0),
        shares:                 acc.shares                 + (item.shares                 ?? 0),
        saved:                  acc.saved                  + (item.saved                  ?? 0),
        video_views:            acc.video_views            + (item.video_views            ?? 0),
        reels_video_watch_time: acc.reels_video_watch_time + (item.reels_video_watch_time ?? 0),
      }),
      zero
    );

    return { ...summary, post_count: filteredList.length, totals };
  }, [summary, filteredList]);

  // Auto-load on customer change using the current date range defaults
  // startDate/endDate intentionally excluded from deps — Apply button handles date changes
  useEffect(() => {
    stopCollecting();
    setCollecting(false);
    setCollectMessage(null);
    if (selectedCustomerId) {
      loadAnalytics(selectedCustomerId, startDate, endDate);
    } else {
      setSummary(null);
      setAnalyticsList([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomerId, loadAnalytics, stopCollecting]);

  const handleApply = () => {
    if (!selectedCustomerId) return;
    setLoading(true);
    loadAnalytics(selectedCustomerId, startDate, endDate);
  };

  const handleClear = () => {
    const newStart = defaultStartDate();
    const newEnd = defaultEndDate();
    setStartDate(newStart);
    setEndDate(newEnd);
    if (selectedCustomerId) {
      setLoading(true);
      loadAnalytics(selectedCustomerId, newStart, newEnd);
    }
  };

  const handleRefresh = async () => {
    if (!selectedCustomerId) return;
    setLoading(true);
    await loadAnalytics(selectedCustomerId, startDate, endDate);
  };

  const handleCollectAnalytics = async () => {
    if (!selectedCustomerId || !idToken || collecting || filterPlatform === 'ALL') return;

    stopCollecting();

    const capturedCustomerId = selectedCustomerId;
    const capturedStart = startDate;
    const capturedEnd = endDate;
    const baselineTimestamp = summary?.last_collected_at ?? null;
    const platformLabel = PLATFORM_OPTIONS.find((o) => o.value === filterPlatform)?.label ?? filterPlatform;

    let cancelled = false;
    collectCancelRef.current = () => { cancelled = true; };

    setCollecting(true);
    setCollectMessage(null);

    try {
      await triggerAnalyticsCollection(capturedCustomerId, idToken, {
        platform: filterPlatform,
      });
      setCollectMessage(`Collecting ${platformLabel} data…`);

      let polls = 0;
      const MAX_POLLS = 3; // 30 s max at 10 s intervals

      pollIntervalRef.current = setInterval(async () => {
        if (cancelled) { clearInterval(pollIntervalRef.current!); pollIntervalRef.current = null; return; }
        polls++;
        try {
          const freshSummary = await fetchAnalyticsSummary(capturedCustomerId, idToken, {
            startDate: capturedStart,
            endDate: capturedEnd,
          });
          const advanced =
            freshSummary?.last_collected_at != null &&
            freshSummary.last_collected_at !== baselineTimestamp;

          if (advanced || polls >= MAX_POLLS) {
            clearInterval(pollIntervalRef.current!);
            pollIntervalRef.current = null;
            if (cancelled) return;

            if (advanced) {
              setLoading(true);
              await loadAnalytics(capturedCustomerId, capturedStart, capturedEnd);
              setCollectMessage(`${platformLabel} analytics updated.`);
            } else {
              setCollectMessage(`No current data for ${platformLabel} yet — try again in a few minutes.`);
            }
            setCollecting(false);
            collectTimeoutRef.current = setTimeout(() => setCollectMessage(null), 8000);
          }
        } catch {
          // Transient poll error — keep polling until the cap, then stop
          if (polls >= MAX_POLLS) {
            clearInterval(pollIntervalRef.current!);
            pollIntervalRef.current = null;
            if (cancelled) return;
            setCollectMessage(`No current data for ${platformLabel} yet — try again in a few minutes.`);
            setCollecting(false);
            collectTimeoutRef.current = setTimeout(() => setCollectMessage(null), 8000);
          }
        }
      }, 10000);
    } catch (err: any) {
      if (!cancelled) {
        debug.error('Failed to trigger analytics collection:', err);
        setCollectMessage(err.message || 'Failed to trigger collection.');
        setCollecting(false);
        collectTimeoutRef.current = setTimeout(() => setCollectMessage(null), 5000);
      }
    }
  };

  const activePlatformLabel =
    PLATFORM_OPTIONS.find((o) => o.value === filterPlatform)?.label ?? 'All Platforms';

  // Date range — rendered under the summary widget's "Last updated" line
  const dateControls = (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <div className="min-w-[230px]">
        <DateRangeFilter
          startDate={startDate}
          endDate={endDate}
          onDateChange={(start, end) => { setStartDate(start); setEndDate(end); }}
          onApply={handleApply}
          onClear={handleClear}
        />
      </div>
    </div>
  );

  // AI Visibility view (SGL-140) renders instead of the social report —
  // one Analytics surface, two reports, matching the dashboard design's
  // "Social Posts | AI Visibility" tabs. Pro-only (defense in depth: even if
  // analyticsView somehow ends up "ai" while locked, e.g. a stale downgrade,
  // this still falls through to the social view rather than rendering it).
  if (analyticsView === "ai" && !visibilityManagerLocked) {
    return (
      <div className="space-y-6">
        <AnalyticsViewToggle
          view={analyticsView}
          onChange={setAnalyticsView}
          aiLocked={visibilityManagerLocked}
          onLockedClick={() => setShowVisibilityUpgradeModal(true)}
        />
        <AiVisibilityDashboard />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <AnalyticsViewToggle
        view={analyticsView}
        onChange={setAnalyticsView}
        aiLocked={visibilityManagerLocked}
        onLockedClick={() => setShowVisibilityUpgradeModal(true)}
      />
      <FeatureUpgradeModal
        isOpen={showVisibilityUpgradeModal}
        onClose={() => setShowVisibilityUpgradeModal(false)}
        featureKey="visibility_manager"
      />
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-theme-fg">
          Social Post Performance
        </h2>
      </div>

      {/* Platform availability notice */}
      <div className="bg-theme-surface border border-theme-border rounded-lg p-3 flex items-start gap-2.5 shadow-sm">
        <span className="material-icons text-brand-primary text-base mt-0.5">info</span>
        <div className="text-xs text-theme-fg-muted">
          <strong className="text-theme-fg text-[13px] block mb-1">
            Analytics availability by platform
          </strong>
          <ul className="list-disc list-outside ml-4 space-y-0.5 marker:text-theme-border">
            <li>
              <span className="font-medium text-theme-fg">Facebook, Instagram &amp; YouTube:</span> Fully available.
            </li>
            <li className="text-amber-700 dark:text-amber-500">
              <span className="font-medium">LinkedIn:</span> Business Page posts only (no personal profiles).
            </li>
          </ul>
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-4 text-red-700 dark:text-red-400">
          <div className="flex items-center gap-2">
            <span className="material-icons text-red-500 dark:text-red-400">error</span>
            <p className="flex-1">{error}</p>
            <button
              onClick={() => loadAnalytics(selectedCustomerId!, startDate, endDate)}
              className="ml-2 inline-flex items-center gap-1 rounded-md bg-red-100 dark:bg-red-900/40 px-2.5 py-1 text-xs font-medium text-red-700 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/60 transition-colors"
            >
              <span className="material-icons text-sm">refresh</span>
              Retry
            </button>
          </div>
        </div>
      )}

      {selectedCustomerId && !error && (
        <>
          {/* Toolbar: platform filter + Collect Data side by side */}
          <div className="flex flex-col gap-3 rounded-xl border border-theme-border bg-theme-surface p-4 shadow-sm md:flex-row md:items-center md:justify-between">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="relative min-w-[190px]" ref={platformDropdownRef}>
                <button
                  type="button"
                  id="analytics-platform-dropdown"
                  onClick={() => setPlatformMenuOpen((o) => !o)}
                  className="flex w-full items-center justify-between rounded-lg border border-[var(--color-input-border)] bg-[var(--color-input-bg)] px-4 py-2.5 text-left text-sm font-medium text-theme-fg transition-colors hover:bg-theme-secondary/10 focus:outline-none focus:ring-2 focus:ring-brand-primary"
                  aria-expanded={platformMenuOpen}
                  aria-haspopup="listbox"
                  aria-label="Filter analytics by platform"
                >
                  <span className="truncate">{activePlatformLabel}</span>
                  <span
                    className={`material-icons ml-2 flex-shrink-0 text-zinc-400 transition-transform ${
                      platformMenuOpen ? 'rotate-180' : ''
                    }`}
                  >
                    expand_more
                  </span>
                </button>
                {platformMenuOpen && (
                  <div
                    className="absolute left-0 z-[100] mt-1 max-h-60 min-w-full overflow-auto rounded-md border border-[var(--color-input-border)] bg-theme-surface py-1 shadow-lg ring-1 ring-black ring-opacity-5 custom-scrollbar"
                    role="listbox"
                  >
                    {PLATFORM_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        role="option"
                        aria-selected={filterPlatform === opt.value}
                        className={`flex w-full cursor-pointer px-4 py-2 text-left text-sm transition-all duration-200 ${
                          filterPlatform === opt.value
                            ? 'bg-brand-primary/10 text-brand-primary hover:bg-brand-primary/20'
                            : 'text-theme-fg hover:bg-zinc-100 dark:hover:bg-zinc-800'
                        }`}
                        onClick={() => {
                          setFilterPlatform(opt.value);
                          setPlatformMenuOpen(false);
                        }}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <button
                onClick={() => handleCollectAnalytics()}
                disabled={collecting || loading || filterPlatform === 'ALL'}
                title={
                  filterPlatform === 'ALL'
                    ? 'Select a specific platform to collect data'
                    : `Pull fresh ${activePlatformLabel} analytics from the platform`
                }
                className={`inline-flex items-center gap-1.5 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors ${
                  collecting
                    ? 'bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800 cursor-wait'
                    : filterPlatform === 'ALL'
                    ? 'bg-theme-surface text-theme-fg-muted border border-theme-border cursor-not-allowed opacity-50'
                    : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100 dark:hover:bg-emerald-900/40'
                }`}
              >
                <span className={`material-icons text-base ${collecting ? 'animate-spin' : ''}`}>
                  {collecting ? 'sync' : 'cloud_download'}
                </span>
                {collecting ? 'Collecting…' : 'Collect Data'}
              </button>
            </div>

            <div className="flex flex-col items-end text-xs text-theme-fg-muted">
              <div>
                Last updated:{' '}
                {summary?.last_collected_at
                  ? new Date(summary.last_collected_at).toLocaleString()
                  : 'N/A'}
              </div>
              <div className="mt-1 flex items-center gap-1 opacity-75" title="Data is automatically collected every Monday at 06:00 UTC">
                <span className="material-icons text-[12px]">schedule</span>
                Auto-syncs every Monday
              </div>
            </div>
          </div>

          {filterPlatform === 'ALL' && (
            <p className="text-xs text-theme-fg-muted -mt-3">
              Select a specific platform to enable the Collect Data button.
            </p>
          )}

          {collectMessage && (
            <div className="flex items-center gap-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 px-4 py-2.5 text-sm text-emerald-800 dark:text-emerald-400">
              <span className="material-icons text-emerald-500 dark:text-emerald-400 text-base">info</span>
              {collectMessage}
            </div>
          )}

          {noData && !loading ? (
            <>
              {dateControls}
              <div className="rounded-lg border border-theme-border bg-theme-surface p-8 text-center shadow-sm">
              <span className="material-icons mb-3 text-4xl text-theme-border">bar_chart</span>
              <p className="text-base font-medium text-theme-fg">No current data for this period.</p>
              <p className="mt-1 text-sm text-theme-fg-muted">
                Try a different date range, or select a platform and use Collect Data to pull fresh analytics.
              </p>
              <button
                onClick={() => { setNoData(false); handleRefresh(); }}
                className="mt-4 inline-flex items-center gap-1.5 rounded-lg border border-theme-border bg-theme-surface px-4 py-2 text-sm font-medium text-theme-fg hover:bg-theme-secondary/10 transition-colors"
              >
                <span className="material-icons text-base">refresh</span>
                Try again
              </button>
              </div>
            </>
          ) : (
            <>
              <AnalyticsSummaryWidget
                summary={computedSummary}
                loading={loading}
                activePlatformFilter={filterPlatform}
              />

              <div>
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <h3 className="text-lg font-semibold text-theme-fg">
                    Detailed Post Analytics
                    {filterPlatform !== 'ALL' && (
                      <span className="ml-2 text-sm font-normal text-theme-fg-muted">
                        — {activePlatformLabel}
                      </span>
                    )}
                  </h3>
                  {dateControls}
                </div>
                <PostAnalyticsTable
                  analyticsList={filteredList}
                  loading={loading}
                  activePlatformFilter={filterPlatform}
                />
              </div>
            </>
          )}
        </>
      )}

      {!selectedCustomerId && !loading && (
        <div className="rounded-lg border border-theme-border bg-theme-surface p-12 text-center text-theme-fg-muted shadow-sm">
          <span className="material-icons mb-4 text-5xl text-theme-border">
            insights
          </span>
          <p className="text-lg font-medium text-theme-fg">No Customer Selected</p>
          <p className="mt-2">Please select a customer to view their analytics.</p>
        </div>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
